// controllers/useExperienceController.js
import { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import { useSession } from '../context/SessionContext';

export default function useExperienceController() {
  const { session } = useSession();
  const [experiences, setExperiences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch experiences from the database
  const fetchExperiences = async () => {
    try {
      setLoading(true);
      const { data: experiences, error: experiencesError } = await supabase
        .from('experience')
        .select('*')
        .eq('user_id', session.user.id);

      if (experiencesError) throw experiencesError;

      // Ambil gambar untuk setiap experience
      const experiencesWithImages = await Promise.all(
        experiences.map(async (exp) => {
          const { data: images, error: imagesError } = await supabase
            .from('image')
            .select('path')
            .eq('category_id', exp.id)
            .eq('category', 'experience') // Filter by category

          if (imagesError) throw imagesError;

          // Generate public URL untuk setiap gambar
          const imagesWithPublicUrl = images.map((image) => ({
            ...image,
            publicUrl: supabase.storage.from('document').getPublicUrl(image.path).data.publicUrl,
          }));

          return { ...exp, images: imagesWithPublicUrl };
        })
      );

      setExperiences(experiencesWithImages);
    } catch (error) {
      console.error('Error fetching experiences:', error);
      setError('Failed to fetch experiences');
    } finally {
      setLoading(false);
    }
  };

  // Add a new experience
  const addExperience = async (experienceData, images) => {
    try {
      setLoading(true);

      // Insert experience data
      const { data: experience, error: experienceError } = await supabase
        .from('experience')
        .insert([{ ...experienceData, user_id: session.user.id }])
        .select()
        .single();

      if (experienceError) throw experienceError;

      // Upload new images and insert into image table
      if (images && images.length > 0) {
        await Promise.all(
          images.map(async (image) => {
            // Pastikan ini adalah File baru (bukan URL string)
            if (!(image instanceof File)) {
              return; // Lewati jika bukan File
            }

            const fileExt = image.name.split('.').pop();
            const fileName = `${Math.random()}.${fileExt}`;
            const filePath = `${fileName}`;

            // Upload image to storage
            const { error: uploadError } = await supabase.storage
              .from('document')
              .upload(filePath, image);

            if (uploadError) throw uploadError;

            // Insert image metadata into image table
            await supabase.from('image').insert({
              user_id: session.user.id,
              category: 'experience',
              category_id: experience.id,
              path: filePath,
            });
          })
        );
      }

      // Refresh experiences list
      await fetchExperiences();
    } catch (error) {
      console.error('Error adding experience:', error);
      setError('Failed to add experience');
    } finally {
      setLoading(false);
    }
  };

  // Update an existing experience
  const updateExperience = async (id, experienceData, images) => {
    try {
      setLoading(true);

      // Update experience data
      const { error } = await supabase
        .from('experience')
        .update({ ...experienceData })
        .eq('id', id)
        .single();

      if (error) throw error;

      // Upload new images and insert into image table
      if (images && images.length > 0) {
        await Promise.all(
          images.map(async (image) => {
            const fileExt = image.name.split('.').pop();
            const fileName = `${Math.random()}.${fileExt}`;
            const filePath = `${fileName}`;

            // Upload image to storage
            const { error: uploadError } = await supabase.storage
              .from('document')
              .upload(filePath, image);

            if (uploadError) throw uploadError;

            // Insert image metadata into image table
            await supabase.from('image').insert({
              user_id: session.user.id,
              category: 'experience',
              category_id: id,
              path: filePath,
            });
          })
        );
      }

      // Refresh experiences list
      await fetchExperiences();
    } catch (error) {
      console.error('Error updating experience:', error);
      setError('Failed to update experience');
    } finally {
      setLoading(false);
    }
  };

  // Delete an experience and its associated images
  const deleteExperience = async (id) => {
    try {
      setLoading(true);

      // Fetch associated images
      const { data: images, error: imagesError } = await supabase
        .from('image')
        .select('path')
        .eq('category_id', id);

      if (imagesError) throw imagesError;

      // Delete images from storage
      if (images && images.length > 0) {
        await Promise.all(
          images.map(async (image) => {
            const { error: deleteError } = await supabase.storage
              .from('document')
              .remove([image.path]);

            if (deleteError) throw deleteError;
          })
        );
      }

      // Delete image metadata from database
      await supabase.from('image').delete().eq('category_id', id);

      // Delete experience data
      const { error } = await supabase
        .from('experience')
        .delete()
        .eq('id', id);

      if (error) throw error;

      // Refresh experiences list
      await fetchExperiences();
    } catch (error) {
      console.error('Error deleting experience:', error);
      setError('Failed to delete experience');
    } finally {
      setLoading(false);
    }
  };

  // Delete an image
  const deleteImage = async (imagePath) => {
    try {
      // Hapus gambar dari storage
      const { error: deleteError } = await supabase.storage
        .from('document')
        .remove([imagePath]);

      if (deleteError) throw deleteError;

      // Hapus gambar dari database
      await supabase.from('image').delete().eq('path', imagePath);

      // Refresh experiences list
      await fetchExperiences();
    } catch (error) {
      console.error('Error deleting image:', error);
      throw error;
    }
  };

  // Fetch experiences on component mount
  useEffect(() => {
    if(session){
      setLoading(false);
      fetchExperiences();
    }
  }, [session]);
// }, [session]);

  return {
    experiences,
    loading,
    error,
    addExperience,
    updateExperience,
    deleteExperience,
    deleteImage, // Ekspos fungsi deleteImage
  };
}